import React, { useCallback } from "react";
import makeStyles from "@material-ui/styles/makeStyles";
import { IAttribute } from "constants/product-customizations/customization";
import theme from "styles/theme";
import classNames from "classnames";
import { CUSTOMIZATIONS } from "constants/integration/customizations";
import { ChevronRight } from "@material-ui/icons";
import TrashIcon from "@material-ui/icons/Delete";

type AttributeKeys = "_id" | "name" | "isRequired";

export type TAttributeProps = {
    attribute: Pick<IAttribute, AttributeKeys>;
    imageUrl?: string | undefined;
    isSelected: boolean;
    isOpened: boolean;
    isDeletable: boolean;
    hasError: boolean;
    onDelete?: (id: string) => void;
} & Omit<React.DetailedHTMLProps<React.ButtonHTMLAttributes<HTMLButtonElement>, HTMLButtonElement>, "type">;

export const Attribute: React.FC<TAttributeProps> = ({
    attribute,
    imageUrl,
    isSelected,
    isOpened,
    isDeletable,
    hasError,
    onDelete,
    ...props
}) => {
    const classes = useStyles();

    const { _id: id, name, isRequired } = attribute ?? {};

    const handleDelete = useCallback(() => {
        if (id) {
            onDelete?.(`${id}`);
        }
    }, [onDelete, id]);

    return (
        <button
            {...props}
            className={classNames(props.className ?? {}, classes.button, {
                error: hasError,
            })}
            type="button"
        >
            {
                // TODO: UNCOMMENT WHEN TEMPLATE OVERRIDES ARE AVAILABLE
                // !imageUrl ? null : <img alt={name} className={classes.image} src={imageUrl} />}
            }

            <span className={classes.title}>{name}</span>

            {isRequired && <span className={classes.requiredLabel}>{"*"}</span>}

            <div className={classes.rightPart}>
                {!isRequired && isDeletable && (
                    <span
                        data-attribute-id={`${id}`}
                        onClick={handleDelete}
                        data-test={CUSTOMIZATIONS.DELETE_ATTRIBUTE_BUTTON}
                        className={classes.deleteButton}
                    >
                        <TrashIcon className={classes.deleteButtonIcon} />
                    </span>
                )}
                {<ChevronRight className={isSelected && isOpened ? classes.rotatedArrow : classes.arrow} />}
            </div>
        </button>
    );
};

const arrowStyle = (rotation = 0) => ({
    transform: `rotate(${rotation}deg)`,
    transformOrigin: "50% 50%",
    transition: "300ms ease all",
    fontSize: "30px",
});

const useStyles = makeStyles({
    title: {},
    requiredLabel: {
        color: theme.palette.black,
        fontSize: "14px",
    },
    rightPart: {
        marginLeft: "auto",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        justifyItems: "center",
    },
    deleteButton: {
        display: "flex",
    },
    deleteButtonIcon: {
        color: theme.palette.textColor,
        fontSize: "16px",
    },
    arrow: arrowStyle(),
    rotatedArrow: arrowStyle(90),
    image: {
        display: "block",
        maxHeight: 50,
        maxWidth: 100,
        marginInlineEnd: 24,
    },
    button: {
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        width: "100%",
        paddingBlock: 10,
        paddingInline: 16,
        border: "none",
        backgroundColor: "transparent",
        fontSize: 16,
        fontWeight: 700,
        position: "relative",

        "&:hover": {
            backgroundColor: theme.palette.lightGrey,
        },

        "&.error": {
            backgroundColor: theme.palette.errorLightBackground,
        },
    },
});
